/**
 * Suggested memory entries — learned rules awaiting the clinician's accept/reject,
 * without exposing clinical source evidence.
 */

import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { Check, Lightbulb, Loader2, X } from 'lucide-react';
import { useMemoryErrorToast, useUpdateMemoryEntry } from '../useUserMemoryQueries';
import { memoryCategoryChipVariants, memoryStatusAccentVariants } from '../userMemory.styles';
import { MEMORY_CATEGORY_LABELS, type MemoryEntry } from '../userMemory.types';

interface SuggestedMemoryListProps {
  entries: MemoryEntry[];
}

interface SuggestedMemoryCardProps {
  entry: MemoryEntry;
}

const SuggestedMemoryCard = ({ entry }: SuggestedMemoryCardProps) => {
  const updateMutation = useUpdateMemoryEntry();
  const { toast } = useToast();
  const onError = useMemoryErrorToast();

  const isPending = updateMutation.isPending && updateMutation.variables?.id === entry.id;

  const handleAccept = () => {
    updateMutation.mutate(
      { id: entry.id, payload: { status: 'active' } },
      {
        onSuccess: () =>
          toast({ title: 'Minni samþykkt', description: 'Þessi regla verður notuð í næstu samantektum.' }),
        onError,
      }
    );
  };

  const handleReject = () => {
    updateMutation.mutate(
      { id: entry.id, payload: { status: 'rejected' } },
      {
        onSuccess: () => toast({ title: 'Tillögu hafnað' }),
        onError,
      }
    );
  };

  return (
    <div className={cn(memoryStatusAccentVariants({ status: 'suggested' }))}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex items-center gap-2">
            <span className={cn(memoryCategoryChipVariants({ category: entry.category }))}>
              {MEMORY_CATEGORY_LABELS[entry.category]}
            </span>
            {entry.confirm_only && (
              <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 font-medium text-slate-600 text-xs">
                Krefst staðfestingar
              </span>
            )}
          </div>
          <p className="text-slate-900 text-sm">{entry.instruction_text}</p>
          <p className="text-muted-foreground text-xs">
            {entry.scope === 'global' ? 'Sameiginlegt minni · Þú velur hvort það sé notað' : 'Persónulegt minni'}
          </p>
        </div>
        <div className="flex shrink-0 gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleReject}
            disabled={isPending || updateMutation.isPending}
            aria-label="Hafna"
          >
            {isPending && updateMutation.variables?.payload.status === 'rejected' ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <X className="h-4 w-4" />
            )}
          </Button>
          <Button
            size="sm"
            onClick={handleAccept}
            disabled={isPending || updateMutation.isPending}
            aria-label="Samþykkja"
          >
            {isPending && updateMutation.variables?.payload.status === 'active' ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Check className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export const SuggestedMemoryList = ({ entries }: SuggestedMemoryListProps) => {
  if (entries.length === 0) {
    return null;
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Lightbulb className="h-4 w-4 text-primary" />
        <h3 className="font-medium text-slate-900 text-sm">Tillögur</h3>
      </div>
      <div className="space-y-3">
        {entries.map((entry) => (
          <SuggestedMemoryCard key={entry.id} entry={entry} />
        ))}
      </div>
    </div>
  );
};
